import React, { useState, memo, useEffect } from 'react';
import styled from 'styled-components';

import { WoosmapType } from '@front/common/src/types/commons';
import { EMPTY_ADDRESS } from '@front/common/src/constants/address';
import { filterWoosmapSuggestions } from '@front/common/src/components/woosmap-autocomplete/filter-suggestions';
import { buildWoosmapAddress, isValidWoosmapAddress } from '@front/common/src/utils/address';
import { CommonInputProps } from '@front/common/src/components/input/types';
import {
  AutocompleteResponseItem,
  useAddressDetailQuery,
  useAddressQuery,
  useGeolocationQuery,
} from '@front/common/src/queries/woosmap/address-queries';
import { TrackLocationEvent } from '@front/common/src/types/tracking';
import { WoosmapAutocompleteCommonProps } from '@front/common/src/components/woosmap-autocomplete/types';

import { translate } from '@front/i18n';

import { Suggestion, autocompleteDropdownStyle } from '../../styles/autocomplete';
import Text from '../base-text/variants/text';
import Input from '../input/input';

const t = translate('components.autocomplete_address');

const AutoCompleteDropdown = styled.div`
  ${autocompleteDropdownStyle}
`;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

export type WoosmapAutocompleteProps = WoosmapAutocompleteCommonProps &
  Pick<CommonInputProps, 'error'> & {
    excludedTypes?: WoosmapType[];
    label?: string;
    onEvent?: (event: TrackLocationEvent) => void;
    shouldSortByGeolocation?: boolean;
  };

export const WoosmapAutocomplete = ({
  address,
  apiKey,
  error,
  excludedTypes = [],
  id = 'autocompleteAddress',
  label = t('inputs.address.label'),
  name = 'addressAutocomplete',
  onBlur,
  onChange,
  onError,
  onFocus,
  onEvent,
  onIncompleteAddress,
  userInputAddress,
  shouldSortByGeolocation = false,
}: WoosmapAutocompleteProps) => {
  const [isTouchedOutside, setIsTouchedOutside] = useState(false);
  const geolocation = useGeolocationQuery({ apiKey, enabled: shouldSortByGeolocation });
  const { data: suggestions, isSuccess: isAddressSuccess } = useAddressQuery({
    keyword: userInputAddress,
    location: geolocation?.latitude ? `${geolocation.latitude},${geolocation.longitude}` : undefined,
    apiKey,
  });

  const [selectedSuggestion, setSelectedSuggestion] = useState<AutocompleteResponseItem>();

  // This query is triggered only when we have a selectedSuggestion.public_id
  // So this means, it will be enabled only when a suggestion on the dropdown is clicked
  const { addressDetail } = useAddressDetailQuery({ publicId: selectedSuggestion?.public_id, apiKey });

  useEffect(() => {
    if (addressDetail) {
      if (isValidWoosmapAddress(addressDetail)) {
        onChange('userInputAddress', '');
        onChange('address', buildWoosmapAddress(addressDetail));
        onError('addressError', '');
      } else {
        onError('addressError', t('.errors.address'));
        onIncompleteAddress?.();
        if (onEvent) onEvent('UnableToGeolocalize');
      }
    }
    // We only want this effect to be triggered when the addressDetail change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressDetail]);

  const handleInputChange = (value: string) => {
    onChange('address', EMPTY_ADDRESS);
    onChange('userInputAddress', value);
    setSelectedSuggestion(undefined);
    setIsTouchedOutside(false);
  };

  const handleSelect = (suggestion: AutocompleteResponseItem) => () => {
    setIsTouchedOutside(true);
    setSelectedSuggestion(suggestion);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsTouchedOutside(true);
    onBlur?.();
  };

  const filteredSuggestions = filterWoosmapSuggestions({ suggestions, excludedTypes });

  return (
    <Container>
      <Input
        autoComplete='off'
        error={error}
        id={id}
        label={label}
        name={name}
        onBlur={handleBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value)}
        onFocus={onFocus}
        value={userInputAddress.length > 0 ? userInputAddress : address.address}
      />
      {filteredSuggestions && filteredSuggestions.length > 0 && !isTouchedOutside && isAddressSuccess && (
        <AutoCompleteDropdown className='autocomplete-dropdown-container' role='listbox'>
          {filteredSuggestions.map((suggestion) => (
            // Here, it’s different from mobile. If we use onClick, the input’s onBlur will trigger first,
            // execute setIsTouchedOutside(true), and hide the suggestions. Then, the onClick logic of the suggestion
            // won’t have time to execute. We need to use onMouseDown instead of onClick here.
            <Suggestion key={suggestion.public_id} onMouseDown={handleSelect(suggestion)} role='option'>
              <Text>{suggestion.description}</Text>
            </Suggestion>
          ))}
        </AutoCompleteDropdown>
      )}
    </Container>
  );
};

export default memo(WoosmapAutocomplete);
